import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";

import LocalStorageService from "services/LocalStorageService";

const ChallengesCacheContext = createContext<any>(undefined);

/** Bump when the persisted shape changes to invalidate old snapshots. */
const PERSISTED_CACHE_VERSION = 2;
const PERSISTED_CACHE_KEY = "ws.challengesCache.v2";
/** Data older than this is discarded entirely to avoid showing very stale content. */
const MAX_PERSIST_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Debounce writes so we don't hit localStorage on every small state update. */
const PERSIST_DEBOUNCE_MS = 400;
/** Trim feed cache so we never blow past localStorage quota. */
const MAX_PERSISTED_FEED = 80;

/** In-memory TTL used by `isCacheValid`. */
const CACHE_VALIDITY_DURATION = 5 * 60 * 1000;

const persistedList = (list) => (Array.isArray(list) ? list : []);

/**
 * Read the last persisted snapshot synchronously so the first paint already has data.
 * @return {Object|null} Parsed snapshot object, or `null` when nothing usable is available.
 */
const readPersistedSnapshot = () => {
  try {
    const raw = LocalStorageService.get(PERSISTED_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== PERSISTED_CACHE_VERSION) return null;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > MAX_PERSIST_AGE_MS) return null;
    return parsed;
  } catch (e) {
    console.warn("Failed to read persisted challenges cache:", e);
    return null;
  }
};

interface Props {
  children: React.ReactNode;
}

function ChallengesCacheProvider({children}: Props) {
  const persistedRef = useRef(typeof window !== "undefined" ? readPersistedSnapshot() : null);
  const persisted = persistedRef.current;

  const [cachedFeedActivities, setCachedFeedActivities] = useState(() =>
    Array.isArray(persisted?.feedActivities) ? persisted.feedActivities : []
  );

  const allJoinedChallenges = useRef(persistedList(persisted?.joinedChallenges));

  const [openChallenges, setOpenChallenges] = useState(() => persistedList(persisted?.openChallenges));
  const openChallengesRef = useRef(openChallenges);

  const [availableChallenges, setAvailableChallenges] = useState(() => persistedList(persisted?.availableChallenges));
  const availableChallengesRef = useRef(availableChallenges);

  const [privateGroups, setPrivateGroups] = useState(() => persistedList(persisted?.privateGroups));
  const privateGroupsRef = useRef(privateGroups);

  const [publicGroups, setPublicGroups] = useState(() => persistedList(persisted?.publicGroups));
  const publicGroupsRef = useRef(publicGroups);

  const [closedChallenges, setClosedChallenges] = useState(() => persistedList(persisted?.closedChallenges));
  const closedChallengesRef = useRef(closedChallenges);

  /**
   * In-memory freshness timestamps. Hydrated from disk so `isCacheValid` lets us skip
   * network refreshes right after a reload when the data was recent.
   */
  const cacheTimestamps = useRef({
    joinedChallenges: persisted?.timestamps?.joinedChallenges || 0,
    availableChallenges: persisted?.timestamps?.availableChallenges || 0,
    feedActivities: persisted?.timestamps?.feedActivities || 0
  });

  const persistTimer = useRef(null);

  /** Keep refs in sync with persisted initial state. */
  useEffect(() => {
    openChallengesRef.current = openChallenges;
  }, [openChallenges]);
  useEffect(() => {
    availableChallengesRef.current = availableChallenges;
  }, [availableChallenges]);
  useEffect(() => {
    privateGroupsRef.current = privateGroups;
  }, [privateGroups]);
  useEffect(() => {
    publicGroupsRef.current = publicGroups;
  }, [publicGroups]);
  useEffect(() => {
    closedChallengesRef.current = closedChallenges;
  }, [closedChallenges]);

  /** Debounced write to localStorage; cheap enough to call after every update. */
  const schedulePersist = useCallback(() => {
    if (typeof window === "undefined") return;
    if (persistTimer.current) {
      clearTimeout(persistTimer.current);
    }
    persistTimer.current = setTimeout(() => {
      try {
        // Feed items are `Activity` class instances; getters on the prototype are not
        // captured by `JSON.stringify`. Snapshot the values we need on plain properties
        // so cached cards render correctly after a reload.
        const feed = Array.isArray(cachedFeedActivities)
          ? cachedFeedActivities.slice(0, MAX_PERSISTED_FEED).map((a) => {
              try {
                return {
                  ...a,
                  waterSportName: a.waterSportName ?? null,
                  dateInDDMMMYYYYATHMMWithM: a.dateInDDMMMYYYYATHMMWithM ?? null,
                  name: a.name ?? null,
                  secondaryName: a.secondaryName ?? null,
                  location: a.location ?? null,
                  comment: a.comment ?? null
                };
              } catch {
                return {...a};
              }
            })
          : [];
        const snapshot = {
          version: PERSISTED_CACHE_VERSION,
          savedAt: Date.now(),
          timestamps: {...cacheTimestamps.current},
          joinedChallenges: allJoinedChallenges.current,
          openChallenges: openChallengesRef.current,
          closedChallenges: closedChallengesRef.current,
          privateGroups: privateGroupsRef.current,
          availableChallenges: availableChallengesRef.current,
          publicGroups: publicGroupsRef.current,
          feedActivities: feed
        };
        LocalStorageService.set(PERSISTED_CACHE_KEY, JSON.stringify(snapshot));
      } catch (e) {
        // Quota errors etc. — drop the snapshot rather than crashing the app.
        console.warn("Failed to persist challenges cache:", e);
      }
    }, PERSIST_DEBOUNCE_MS);
  }, [cachedFeedActivities]);

  const clearCache = useCallback(() => {
    setCachedFeedActivities([]);
    allJoinedChallenges.current = [];
    setOpenChallenges([]);
    openChallengesRef.current = [];
    setAvailableChallenges([]);
    availableChallengesRef.current = [];
    setPrivateGroups([]);
    privateGroupsRef.current = [];
    setPublicGroups([]);
    publicGroupsRef.current = [];
    setClosedChallenges([]);
    closedChallengesRef.current = [];
    cacheTimestamps.current = {joinedChallenges: 0, availableChallenges: 0, feedActivities: 0};
    try {
      LocalStorageService.remove(PERSISTED_CACHE_KEY);
    } catch {
      // ignore
    }
  }, []);

  const updateFeedCache = useCallback(
    (activities) => {
      setCachedFeedActivities(activities);
      cacheTimestamps.current.feedActivities = Date.now();
      schedulePersist();
    },
    [schedulePersist]
  );

  const updateJoinedChallenges = useCallback(
    (challenges) => {
      allJoinedChallenges.current = challenges;
      cacheTimestamps.current.joinedChallenges = Date.now();
      schedulePersist();
    },
    [schedulePersist]
  );

  const updateOpenChallenges = useCallback(
    (challenges) => {
      setOpenChallenges(challenges);
      openChallengesRef.current = challenges;
      schedulePersist();
    },
    [schedulePersist]
  );

  const updateAvailableChallenges = useCallback(
    (challenges) => {
      setAvailableChallenges(challenges);
      availableChallengesRef.current = challenges;
      cacheTimestamps.current.availableChallenges = Date.now();
      schedulePersist();
    },
    [schedulePersist]
  );

  const updatePrivateGroups = useCallback(
    (groups) => {
      setPrivateGroups(groups);
      privateGroupsRef.current = groups;
      schedulePersist();
    },
    [schedulePersist]
  );

  const updatePublicGroups = useCallback(
    (groups) => {
      setPublicGroups(groups);
      publicGroupsRef.current = groups;
      schedulePersist();
    },
    [schedulePersist]
  );

  const updateClosedChallenges = useCallback(
    (challenges) => {
      setClosedChallenges(challenges);
      closedChallengesRef.current = challenges;
      schedulePersist();
    },
    [schedulePersist]
  );

  const isCacheValid = useCallback((cacheType) => {
    return Date.now() - cacheTimestamps.current[cacheType] < CACHE_VALIDITY_DURATION;
  }, []);

  const updateCacheTimestamp = useCallback(
    (cacheType) => {
      cacheTimestamps.current[cacheType] = Date.now();
      schedulePersist();
    },
    [schedulePersist]
  );

  const getCacheTimestamp = useCallback((cacheType) => {
    return cacheTimestamps.current[cacheType];
  }, []);

  const hasCachedData = useCallback(
    (cacheType) => {
      switch (cacheType) {
        case "joinedChallenges":
          return allJoinedChallenges.current.length > 0;
        case "availableChallenges":
          return availableChallengesRef.current.length > 0;
        case "feedActivities":
          return cachedFeedActivities.length > 0;
        default:
          return false;
      }
    },
    [cachedFeedActivities]
  );

  const values = useMemo(
    () => ({
      cachedFeedActivities,
      setCachedFeedActivities,
      allJoinedChallenges,
      openChallenges,
      setOpenChallenges,
      openChallengesRef,
      availableChallenges,
      setAvailableChallenges,
      availableChallengesRef,
      privateGroups,
      setPrivateGroups,
      privateGroupsRef,
      publicGroups,
      setPublicGroups,
      publicGroupsRef,
      closedChallenges,
      setClosedChallenges,
      closedChallengesRef,
      clearCache,
      updateFeedCache,
      updateJoinedChallenges,
      updateOpenChallenges,
      updateAvailableChallenges,
      updatePrivateGroups,
      updatePublicGroups,
      updateClosedChallenges,
      isCacheValid,
      updateCacheTimestamp,
      getCacheTimestamp,
      hasCachedData
    }),
    [
      cachedFeedActivities,
      openChallenges,
      availableChallenges,
      privateGroups,
      publicGroups,
      closedChallenges,
      clearCache,
      updateFeedCache,
      updateJoinedChallenges,
      updateOpenChallenges,
      updateAvailableChallenges,
      updatePrivateGroups,
      updatePublicGroups,
      updateClosedChallenges,
      isCacheValid,
      updateCacheTimestamp,
      getCacheTimestamp,
      hasCachedData
    ]
  );

  return <ChallengesCacheContext.Provider value={values}>{children}</ChallengesCacheContext.Provider>;
}

function useChallengesCache() {
  const context = useContext(ChallengesCacheContext);
  if (!context) {
    throw new Error("useChallengesCache must be used within a ChallengesCacheProvider");
  }
  return context;
}

export {ChallengesCacheProvider, useChallengesCache};
